import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Spin, message } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import AssetApi from "../../api/AssetApi";
import YextApi, { ListingPhotoSource, MemberImage, memberImageSrc } from "../../api/YextApi";
import { AssetDto } from "../../generated/dashboard-client";
import { env } from "../../env/env";
import "./ChoosePhotoModal.scss";

export interface ChosenPhoto {
  source: ListingPhotoSource;
  imageId?: string;
  imageUrl: string;
  altText?: string;
}

interface Props {
  open: boolean;
  slotLabel: string;
  memberId: number;
  teamId: string;
  locationId: number;
  onClose: () => void;
  onInsert: (photo: ChosenPhoto) => void;
}

type Tab = "uploads" | "brand";

const BRAND_CATEGORIES = ["GRAPHICS", "PHOTOS"];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

function displayName(name: string): string {
  return name.replace(/-[a-zA-Z0-9]{8,}(\.[^.]+)?$/, "$1");
}

export function ChoosePhotoModal({ open, slotLabel, memberId, teamId, locationId, onClose, onInsert }: Props) {
  const [tab, setTab] = useState<Tab>("uploads");
  const [uploads, setUploads] = useState<MemberImage[]>([]);
  const [brand, setBrand] = useState<AssetDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState<ChosenPhoto | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadUploads = useCallback(() => {
    if (!memberId) return;
    setLoading(true);
    YextApi.listMemberImages(memberId)
      .then(({ data }) => setUploads(data.filter((i) => !i.isMp4 && !i.isWebM)))
      .catch(() => message.error("Failed to load your uploads"))
      .finally(() => setLoading(false));
  }, [memberId]);

  const loadBrand = useCallback(() => {
    if (!teamId) return;
    setLoading(true);
    AssetApi.list(teamId)
      .then(({ data }) => setBrand((data || []).filter((a) => BRAND_CATEGORIES.includes(a.category || ""))))
      .catch(() => message.error("Failed to load brand photos"))
      .finally(() => setLoading(false));
  }, [teamId]);

  useEffect(() => {
    if (!open) return;
    setTab("uploads");
    setSelected(null);
    loadUploads();
    loadBrand();
  }, [open, loadUploads, loadBrand]);

  function switchTab(next: Tab) {
    setTab(next);
    setSelected(null);
  }

  function onFilesSelected(files: FileList | null) {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.size > MAX_UPLOAD_BYTES) {
      message.error(`"${file.name}" exceeds the 10MB upload limit`);
      return;
    }
    setUploading(true);
    YextApi.uploadMemberImages(memberId, locationId, [file])
      .then(({ data }) => {
        const added = data.filter((i) => !i.isMp4 && !i.isWebM);
        setUploads((prev) => [...added, ...prev]);
        const first = added[0];
        if (first) setSelected({ source: "UPLOAD", imageId: first.id, imageUrl: memberImageSrc(first), altText: first.altText });
      })
      .catch(() => message.error("Upload failed"))
      .finally(() => setUploading(false));
  }

  function isSelected(url: string): boolean {
    return selected?.imageUrl === url;
  }

  const images = tab === "uploads"
    ? uploads.map((i) => ({ key: i.id, name: i.name, url: memberImageSrc(i), choice: { source: "UPLOAD" as ListingPhotoSource, imageId: i.id, imageUrl: memberImageSrc(i), altText: i.altText } }))
    : brand.map((a) => {
        const url = `${env.s3Path}/${a.path}`;
        return { key: a.id!, name: a.name || "", url, choice: { source: "BRAND" as ListingPhotoSource, imageId: a.id, imageUrl: url, altText: a.altText } };
      });

  return (
    <Modal className="choose-photo-modal" open={open} onCancel={onClose} footer={null} closable={false} width={760} centered destroyOnClose>
      <div className="cp-header">
        <h2>Choose Photo{slotLabel ? ` · ${slotLabel}` : ""}</h2>
        <button type="button" className="cp-close" onClick={onClose} aria-label="Close">
          <CloseOutlined />
        </button>
      </div>

      <div className="cp-tabs">
        <button type="button" className={`cp-tab${tab === "uploads" ? " active" : ""}`} onClick={() => switchTab("uploads")}>
          My Uploads
        </button>
        <button type="button" className={`cp-tab${tab === "brand" ? " active" : ""}`} onClick={() => switchTab("brand")}>
          My Brand Photos
        </button>
      </div>

      <Spin spinning={loading || uploading} tip={uploading ? "Uploading..." : undefined}>
        <div className="cp-grid">
          {tab === "uploads" && (
            <button type="button" className="cp-upload-tile" onClick={() => fileInputRef.current?.click()}>
              <PlusOutlined />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  onFilesSelected(e.target.files);
                  e.target.value = "";
                }}
              />
            </button>
          )}

          {images.map((img) => (
            <button
              type="button"
              key={img.key}
              className={`cp-tile${isSelected(img.url) ? " selected" : ""}`}
              onClick={() => setSelected(img.choice)}
            >
              <div className="cp-thumb" style={{ backgroundImage: `url("${img.url}")` }} />
              <span className="cp-name" title={displayName(img.name)}>{displayName(img.name)}</span>
            </button>
          ))}

          {!loading && images.length === 0 && (
            <p className="cp-empty">
              {tab === "uploads" ? "No uploads yet. Add one with the + tile." : "No brand photos yet. Add graphics in your Brand Kit."}
            </p>
          )}
        </div>
      </Spin>

      <div className="cp-footer">
        <Button type="primary" className="cp-insert" disabled={!selected} onClick={() => selected && onInsert(selected)}>
          Insert photo
        </Button>
      </div>
    </Modal>
  );
}
